import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { AccountUserSelectBox } from '../../../admin/components/user/AccountUserSelectBox';
import { getDisplayName } from '../../../models/NameUtils';
import { AccountUserViewModel } from '../../../viewmodels/AdminViewModels';
import { baseStyleSheet } from '../../styles/styles';
import { ISelectOption } from '../DeprecatedSelect';
import { useConnectionTypes } from '../../../queries/ConnectionType/useConnectionTypes';
import { styleSheet } from './styles';

export interface ISendFromAdminsDropdownProps {
	admins: Api.UserViewModel[];
	userSession: Api.UserSessionContext;
	impersonationContext: Api.IImpersonationContext;
	initialSendFromUser?: Api.IUser;
	/**
	 * Whether to include Connection Types as send from options in the dropdown.
	 * When enabled, connection types will appear as options like "Financial Advisor for the recipient".
	 * Requires the account to have connection types feature enabled.
	 */
	allowConnectionTypes?: boolean;
	sendOnBehalfOf: (
		option: ISelectOption<string | Api.UserViewModel<Api.IUser>>,
		user?: Api.UserViewModel<Api.IUser>
	) => void;
}

export const SendFromAdminsDropdown = (props: ISendFromAdminsDropdownProps) => {
	const connectionTypeQuery = useConnectionTypes({
		enabled: props.allowConnectionTypes && (props.userSession.account.features.connectionTypes?.enabled ?? false),
		impersonationContext: props.impersonationContext,
		refetchOnWindowFocus: false,
	});

	const connectionTypes = connectionTypeQuery.data?.availableConnectionTypes ?? [];

	const onSendFromOptionChanged = (
		option: ISelectOption<string | Api.UserViewModel<Api.IUser>>,
		user?: Api.UserViewModel<Api.IUser>
	) => {
		props.sendOnBehalfOf(option, user);
	};

	const defaultOption = {
		dataContext: Api.SendEmailFrom.ContactOwner,
		id: 'default-user-option-contact-owners',
		text: 'Contact Owners',
	};

	const connectionTypeOptions = connectionTypes.map(connectionType => ({
		dataContext: `connection-type-${connectionType.value}`,
		id: `connection-type-option-${connectionType.value}`,
		text: `${connectionType.label} for the recipient`,
		connectionType: connectionType.value,
	}));

	const initialUserSelectBoxOption = props.initialSendFromUser
		? {
				dataContext: new AccountUserViewModel(props.userSession, props.initialSendFromUser).impersonate(
					props.impersonationContext
				),
				id: `initial-user-option-${props.initialSendFromUser.id}`,
				text: getDisplayName(props.initialSendFromUser),
			}
		: undefined;

	return (
		<div className={css(baseStyleSheet.horizontalStack, styleSheet.sendFromDropdownContainer)}>
			<span>Send from:</span>
			<AccountUserSelectBox
				admins={props.admins}
				defaultOption={defaultOption}
				initialSelectedOption={initialUserSelectBoxOption}
				onOptionSelected={onSendFromOptionChanged}
				pinnedOptions={connectionTypeOptions}
				styles={[styleSheet.sendFromUserSelectBox]}
			/>
		</div>
	);
};
